"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

// 实心三角形图标（左侧挑战项使用）
const TriangleIcon = () => (
	<svg className="h-5 w-5 flex-shrink-0 text-themePrimary400" viewBox="0 0 20 20" fill="currentColor">
		<path d="M10 3L19 17H1L10 3Z" />
	</svg>
);

// 绿色勾选图标（右侧价值项使用）
const CheckIcon = () => (
	<svg className="h-6 w-6 flex-shrink-0 text-themePrimary400" viewBox="0 0 20 20" fill="currentColor">
		<path
			fillRule="evenodd"
			d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
			clipRule="evenodd"
		/>
	</svg>
);

// 左侧挑战项配置接口
interface ChallengeItem {
	titleKey: string;
	descKey: string;
}

// 右侧价值项配置接口
interface ValueItem {
	titleKey: string;
	descKey: string;
}

// 默认左侧挑战列表配置
export const DEFAULT_CHALLENGES: ChallengeItem[] = [
	{ titleKey: "challenge1Title", descKey: "challenge1Desc" },
	{ titleKey: "challenge2Title", descKey: "challenge2Desc" },
	{ titleKey: "challenge3Title", descKey: "challenge3Desc" },
	{ titleKey: "challenge4Title", descKey: "challenge4Desc" },
];

// 默认右侧价值主张列表配置
export const DEFAULT_VALUES: ValueItem[] = [
	{ titleKey: "value1Title", descKey: "value1Desc" },
	{ titleKey: "value2Title", descKey: "value2Desc" },
	{ titleKey: "value3Title", descKey: "value3Desc" },
	{ titleKey: "value4Title", descKey: "value4Desc" },
];

// 组件 Props 接口
export interface WhyChooseUs2Props {
	className?: string;
	// 翻译命名空间
	translationNamespace?: string;
	// 左侧挑战列表配置
	challenges?: ChallengeItem[];
	// 右侧价值主张列表配置
	values?: ValueItem[];
	// 背景颜色类名
	bgClassName?: string;
}

export default function WhyChooseUs2({
	translationNamespace = "whyChooseUs2",
	challenges = DEFAULT_CHALLENGES,
	values = DEFAULT_VALUES,
	bgClassName = "bg-[#f6ffde]",
	className,
}: WhyChooseUs2Props) {
	const t = useTranslations(translationNamespace);

	return (
		<section className={cn(`w-full ${bgClassName} py-8 sm:py-10 md:py-12 lg:py-16`, className)}>
			<div className="container">
				{/* 顶部标题区域 - 居中显示 */}
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					{/* 主标题 */}
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					{/* 副标题 */}
					<p className="text-sm leading-relaxed text-black md:text-base">{t("subtitle")}</p>
				</div>

				{/* 两栏对比布局 */}
				<div className="grid gap-8 lg:grid-cols-2 lg:gap-12 xl:gap-16">
					{/* 左侧 - 常见挑战与隐藏成本 */}
					<div>
						{/* 列标题 */}
						<h3 className="mb-8 text-lg font-bold text-themePrimary400 sm:text-xl md:text-2xl">
							{t("challengesTitle")}
						</h3>
						{/* 挑战列表 */}
						<div className="space-y-6">
							{challenges.map((item, index) => (
								<div key={index} className="flex gap-3">
									{/* 三角形图标 */}
									<div className="mt-0.5 flex-shrink-0">
										<TriangleIcon />
									</div>
									{/* 内容 */}
									<div>
										<h4 className="mb-1 text-base font-semibold text-black sm:text-lg">{t(item.titleKey)}</h4>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* 右侧 - 我们的工程价值主张 */}
					<div>
						{/* 列标题 */}
						<h3 className="mb-8 text-lg font-bold text-black sm:text-xl md:text-2xl">{t("valuesTitle")}</h3>
						{/* 价值列表 */}
						<div className="space-y-6">
							{values.map((item, index) => (
								<div key={index} className="flex gap-3">
									{/* 对勾图标 */}
									<div className="mt-0.5 flex-shrink-0">
										<CheckIcon />
									</div>
									{/* 内容 */}
									<div>
										<h4 className="mb-1 text-base font-semibold text-black sm:text-lg">{t(item.titleKey)}</h4>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				{/* 底部引用文字 */}
				<div className="mt-12 md:mt-16">
					<p className="text-center text-base font-bold !leading-relaxed text-black md:text-lg lg:text-xl">
						"{t("quote")}"
					</p>
				</div>
			</div>
		</section>
	);
}
